import { useEffect, useRef, useState } from 'react';

import api from '../../services/api';

// Every plan members have posted, inside the Moderation tab (her ask 2026-10-08:
// one place for plans and their reports, no new tab). Take down / restore use the
// same PATCH /user-guest-lists/:id/status the report actions use.
// Backend: GET /table-invites/admin/plans.

const card = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 14 };
const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' });
const chip = (bg, fg) => ({ background: bg, color: fg, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 });
const seg = (on) => ({
  background: on ? 'rgba(255,255,255,0.12)' : 'transparent',
  color: on ? '#fff' : 'rgba(255,255,255,.55)',
  border: '1px solid ' + (on ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)'),
  borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
});

const STATUS_CHIP = {
  removed: { label: 'taken down', style: chip('rgba(255,110,110,0.14)', '#ff8d8d') },
  deleted: { label: 'deleted by host', style: chip('rgba(255,255,255,0.08)', 'rgba(255,255,255,.55)') },
};

const when = (ms) => {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
};

const AllPlans = () => {
  const [scope, setScope] = useState('upcoming');
  const [status, setStatus] = useState('all');
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [plans, setPlans] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // Only the latest request may write: a slow answer for old filters is dropped.
  const requestId = useRef(0);

  const load = async (nextPage = 0) => {
    const mine = ++requestId.current;
    setLoading(true);
    try {
      const res = await api.get('/table-invites/admin/plans', { params: { scope, status, q: search, page: nextPage } });
      if (mine !== requestId.current) return;
      const rows = Array.isArray(res.data?.plans) ? res.data.plans : [];
      setPlans((prev) => (nextPage === 0 ? rows : [...prev, ...rows]));
      setTotal(res.data?.total || 0);
      setHasMore(!!res.data?.has_more);
      setPage(nextPage);
    } catch (e) {
      console.log('load plans error', e?.response?.status);
    } finally {
      if (mine === requestId.current) setLoading(false);
    }
  };

  useEffect(() => { load(0); }, [scope, status, search]);

  const setPlanStatus = async (plan, next) => {
    const ask = next === 'removed'
      ? `Take down “${plan.name || 'this plan'}”? It drops off the row, the map and search. You can put it back.`
      : `Put “${plan.name || 'this plan'}” back up?`;
    if (!window.confirm(ask)) return;
    setBusyId(plan._id);
    try {
      await api.patch(`/user-guest-lists/${plan._id}/status`, { status: next });
      setPlans((prev) => prev.map((p) => (p._id === plan._id ? { ...p, status: next } : p)));
    } catch (e) {
      alert('Could not update the plan. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 860 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['upcoming', 'Upcoming'], ['past', 'Past'], ['all', 'All dates']].map(([id, label]) => (
            <button key={id} style={seg(scope === id)} onClick={() => setScope(id)}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['all', 'Any status'], ['active', 'Live'], ['removed', 'Taken down'], ['deleted', 'Deleted by host']].map(([id, label]) => (
            <button key={id} style={seg(status === id)} onClick={() => setStatus(id)}>{label}</button>
          ))}
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); setSearch(query.trim()); }}
          style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          <input
            id="all-plans-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name or venue"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', padding: '7px 10px', fontSize: 13, width: 200 }}
          />
          <button type="submit" style={btn('rgba(79,48,234,0.6)')}>Search</button>
        </form>
      </div>

      <div style={{ color: 'rgba(255,255,255,.45)', fontSize: 13 }}>
        {loading && plans.length === 0 ? 'Loading…' : `${total} plan${total === 1 ? '' : 's'}`}
      </div>

      {!loading && plans.length === 0 && (
        <div style={{ ...card, padding: 40, textAlign: 'center', color: 'rgba(255,255,255,.4)' }}>
          No plans match. Plans members post from the app or the 866 show up here.
        </div>
      )}

      {plans.map((p) => {
        const live = !p.status || p.status === 'active';
        const statusChip = STATUS_CHIP[p.status];
        return (
          <div key={p._id} style={{ ...card, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            {p.image_url ? (
              <img src={p.image_url} alt="" style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'cover', flex: 'none' }} />
            ) : (
              <div style={{ width: 64, height: 64, borderRadius: 10, flex: 'none', background: 'rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,.3)', fontSize: 11 }}>no flyer</div>
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ color: '#fff', fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.name || 'Untitled plan'}
              </div>
              <div style={{ color: 'rgba(255,255,255,.5)', fontSize: 12, marginTop: 2 }}>
                {[p.venue_name, when(p.date), p.market === 'nyc' ? 'NYC' : 'Miami'].filter(Boolean).join(' · ')}
              </div>
              <div style={{ color: 'rgba(255,255,255,.5)', fontSize: 12, marginTop: 2 }}>
                Host: {p.host?.name || 'unknown'} · {p.guests} going
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                {p.open_reports > 0 && (
                  <span style={chip('rgba(255,110,110,0.2)', '#ff8d8d')}>
                    {p.open_reports} open report{p.open_reports === 1 ? '' : 's'}
                  </span>
                )}
                {statusChip && <span style={statusChip.style}>{statusChip.label}</span>}
                {p.is_private && <span style={chip('rgba(255,255,255,0.08)', 'rgba(255,255,255,.6)')}>approval required</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <a href={`https://getzona.app/plans/${p._id}`} target="_blank" rel="noreferrer" style={{ ...btn('rgba(255,255,255,0.08)'), textDecoration: 'none' }}>Open</a>
              {live && (
                <button disabled={busyId === p._id} style={btn('#c0392b')} onClick={() => setPlanStatus(p, 'removed')}>Take down</button>
              )}
              {p.status === 'removed' && (
                <button disabled={busyId === p._id} style={btn('rgba(79,48,234,0.7)')} onClick={() => setPlanStatus(p, 'active')}>Restore</button>
              )}
            </div>
          </div>
        );
      })}

      {hasMore && (
        <button disabled={loading} style={{ ...btn('rgba(255,255,255,0.08)'), alignSelf: 'center' }} onClick={() => load(page + 1)}>
          {loading ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
};

export default AllPlans;
